import { useState, useEffect } from 'react';
import { Mail, Send, Loader2, CheckCircle2, XCircle, Settings, Server, Shield, Save, Eye, EyeOff, Edit, Plus, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import EmailTemplateModal from './EmailTemplateModal';

interface EmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  html_template: string;
  text_template?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface SMTPSettings {
  id?: string;
  host: string;
  port: number;
  username: string;
  password: string;
  from_email: string;
  from_name: string;
}

export default function EmailSettings() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testMessage, setTestMessage] = useState('');
  const [connectionTesting, setConnectionTesting] = useState(false);
  const [connectionResult, setConnectionResult] = useState<'success' | 'error' | null>(null);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);

  const [smtpSettings, setSmtpSettings] = useState<SMTPSettings>({
    host: 'smtp.office365.com',
    port: 587,
    username: 'no-reply@kermarrec.fr',
    password: 'Kermarrec35*',
    from_email: 'no-reply@kermarrec.fr',
    from_name: 'Kermarrec Notification'
  });

  useEffect(() => {
    loadEmailTemplates();
    loadSMTPSettings();
  }, []);

  useEffect(() => {
    if (templates.length === 0) {
      loadEmailTemplates();
    }
  }, [templates.length]);

  const loadEmailTemplates = async () => {
    try {
      console.log('Loading email templates...');
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error loading email templates:', error);
        throw error;
      }
      
      console.log('Email templates loaded:', data);
      setTemplates(data || []);
    } catch (err: any) {
      console.error('Error loading email templates:', err);
    }
  };

  const loadSMTPSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('smtp_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        setSmtpSettings(data);
      }
    } catch (err: any) {
      console.error('Error loading SMTP settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveResult(null);
    setSaveMessage('');

    try {
      // Validate settings
      if (!smtpSettings.host || !smtpSettings.username || !smtpSettings.password || 
          !smtpSettings.from_email || !smtpSettings.from_name) {
        throw new Error('Tous les champs sont obligatoires');
      }

      if (smtpSettings.port < 1 || smtpSettings.port > 65535) {
        throw new Error('Le port doit être entre 1 et 65535');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(smtpSettings.from_email)) {
        throw new Error('Format d\'email invalide pour l\'adresse expéditeur');
      }

      // First, delete all existing configurations to ensure only one exists
      await supabase
        .from('smtp_settings')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all rows

      // Then insert the new configuration
      const { error } = await supabase
        .from('smtp_settings')
        .insert({
          ...smtpSettings,
          id: undefined, // Let the database generate a new ID
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setSaveResult('success');
      setSaveMessage('Configuration SMTP sauvegardée avec succès');
      
      // Reload settings to get the ID if it was a new record
      await loadSMTPSettings();
    } catch (error: any) {
      console.error('Error saving SMTP settings:', error);
      setSaveResult('error');
      setSaveMessage(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setConnectionTesting(true);
    setConnectionResult(null);
    setConnectionMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('test-smtp-connection', {
        body: {}
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setConnectionResult('success');
        setConnectionMessage('Connexion SMTP réussie !');
      } else {
        throw new Error(data?.error || 'Erreur de connexion inconnue');
      }
    } catch (error: any) {
      console.error('Error testing SMTP connection:', error);
      setConnectionResult('error');
      setConnectionMessage(error.message || 'Erreur lors du test de connexion');
    } finally {
      setConnectionTesting(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setTestResult('error');
      setTestMessage('Veuillez saisir une adresse email');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setTestMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: testEmail,
          subject: 'Test d\'envoi depuis K-PROD',
          html: `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <style>
                  body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                  .container { max-width: 600px; margin: 0 auto; padding: 20px; }
                  .header { background: linear-gradient(135deg, #3B82F6, #1E40AF); color: white; padding: 20px; border-radius: 8px 8px 0 0; }
                  .content { background: #f8f9fa; padding: 20px; border-radius: 0 0 8px 8px; }
                  .footer { text-align: center; margin-top: 20px; color: #6b7280; font-size: 12px; }
                </style>
              </head>
              <body>
                <div class="container">
                  <div class="header">
                    <h1>🧪 Email de Test</h1>
                    <p>Test d'envoi depuis K-PROD</p>
                  </div>
                  <div class="content">
                    <p>Bonjour,</p>
                    <p>Ceci est un email de test envoyé depuis K-PROD pour vérifier la configuration SMTP.</p>
                    <p>Si vous recevez cet email, la configuration fonctionne correctement !</p>
                    <p>Date d'envoi : ${new Date().toLocaleString('fr-FR')}</p>
                  </div>
                  <div class="footer">
                    <p>Cet email a été envoyé automatiquement par K-PROD - Kermarrec</p>
                  </div>
                </div>
              </body>
            </html>
          `,
          text: `Test d'envoi depuis K-PROD\n\nCeci est un email de test pour vérifier la configuration SMTP.\nDate d'envoi : ${new Date().toLocaleString('fr-FR')}`
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setTestResult('success');
        setTestMessage('Email de test envoyé avec succès !');
      } else {
        throw new Error(data?.error || 'Erreur d\'envoi inconnue');
      }
    } catch (error: any) {
      console.error('Error sending test email:', error);
      setTestResult('error');
      setTestMessage(error.message || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Configuration Email</h2>
      </div>

      {/* Gestion des templates d'emails */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-medium text-gray-900">Templates d'emails</h3>
          </div>
        </div>

        <div className="space-y-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">
                    {template.name.includes('new_transaction') ? '📝' :
                     template.name.includes('status_change') ? '🔄' :
                     template.name.includes('regularization_request') ? '⚠️' :
                     template.name.includes('regularization_approval') ? '✅' :
                     template.name.includes('objective_reminder') ? '🎯' : '📧'}
                  </span>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {template.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {template.description || 'Aucune description'}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedTemplate(template);
                  setIsTemplateModalOpen(true);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                <Edit size={16} />
                <span>Modifier</span>
              </button>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun template d'email configuré</p>
            </div>
          )}
        </div>
      </div>

      {/* Configuration SMTP */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Settings className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Paramètres SMTP</h3>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Sauvegarde...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Sauvegarder</span>
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serveur SMTP
              </label>
              <input
                type="text"
                value={smtpSettings.host}
                onChange={(e) => setSmtpSettings(prev => ({ ...prev, host: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder="smtp.office365.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Port
              </label>
              <input
                type="number"
                value={smtpSettings.port}
                onChange={(e) => setSmtpSettings(prev => ({ ...prev, port: parseInt(e.target.value) || 587 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                min="1"
                max="65535"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom d'utilisateur
              </label>
              <input
                type="email"
                value={smtpSettings.username}
                onChange={(e) => setSmtpSettings(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder="no-reply@kermarrec.fr"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={smtpSettings.password}
                  onChange={(e) => setSmtpSettings(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email expéditeur
              </label>
              <input
                type="email"
                value={smtpSettings.from_email}
                onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder="no-reply@kermarrec.fr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom d'affichage
              </label>
              <input
                type="text"
                value={smtpSettings.from_name}
                onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder="Kermarrec Notification"
              />
            </div>
          </div>
        </div>

        {/* Message de sauvegarde */}
        {saveResult && (
          <div className={`mt-4 p-4 rounded-lg flex items-center space-x-2 ${
            saveResult === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {saveResult === 'success' ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{saveMessage}</span>
          </div>
        )}

        {/* Avertissement de sécurité */}
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Note de sécurité</p>
              <p className="text-sm text-amber-700">
                Le mot de passe est stocké en texte clair dans la base de données. 
                Assurez-vous que seuls les administrateurs ont accès à cette configuration.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test de connexion */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Server className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Test de connexion SMTP</h3>
          </div>
          <button
            onClick={handleTestConnection}
            disabled={connectionTesting}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {connectionTesting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Test en cours...</span>
              </>
            ) : (
              <>
                <Server className="w-5 h-5" />
                <span>Tester la connexion</span>
              </>
            )}
          </button>
        </div>

        {connectionResult && (
          <div className={`p-4 rounded-lg flex items-center space-x-2 ${
            connectionResult === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {connectionResult === 'success' ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{connectionMessage}</span>
          </div>
        )}
      </div>
      
      {/* Test d'envoi d'email */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Send className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Test d'envoi d'email</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse email de test
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              placeholder="test@example.com"
            />
          </div>

          <button
            onClick={handleTestEmail}
            disabled={testing || !testEmail}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Envoi en cours...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Envoyer un email de test</span>
              </>
            )}
          </button>

          {testResult && (
            <div className={`p-4 rounded-lg flex items-center space-x-2 ${
              testResult === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {testResult === 'success' ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              <span>{testMessage}</span>
            </div>
          )}
        </div>
      </div>

      {/* Configuration actuelle */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration actuelle</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Server className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Serveur SMTP</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{smtpSettings.host}:{smtpSettings.port}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Mail className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Email expéditeur</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{smtpSettings.from_email}</p>
          </div>
        </div>
      </div>

      {/* Templates disponibles */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Templates d'emails disponibles</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2">📝 Nouvelle Transaction</h4>
            <p className="text-sm text-gray-600">
              Notification automatique lors de la création d'une nouvelle transaction
            </p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-medium text-purple-800 mb-2">🔄 Changement de Statut</h4>
            <p className="text-sm text-gray-600">
              Notification lors du passage d'un statut à un autre (potentiel → compromis, etc.)
            </p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h4 className="font-medium text-red-800 mb-2">⚠️ Demande de Régularisation</h4>
            <p className="text-sm text-gray-600">
              Alerte aux administrateurs lors d'une demande de régularisation
            </p>
          </div>
          
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-medium text-amber-800 mb-2">🎯 Rappel d'Objectifs</h4>
            <p className="text-sm text-gray-600">
              Rappel périodique des performances et objectifs aux négociateurs
            </p>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-medium text-green-800 mb-2">✅ Validation de Régularisation</h4>
            <p className="text-sm text-gray-600">
              Notification de validation ou rejet d'une demande de régularisation
            </p>
          </div>

          <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
            <h4 className="font-medium text-teal-800 mb-2">🏆 Rapport de Performance</h4>
            <p className="text-sm text-gray-600">
              Rapport mensuel des performances et classements
            </p>
          </div>
        </div>
      </div>

      {/* Instructions d'utilisation */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Instructions d'utilisation</h3>
        
        <div className="space-y-4 text-sm text-gray-700">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</div>
            <div>
              <p className="font-medium">Configurer les paramètres SMTP</p>
              <p>Modifiez les paramètres ci-dessus selon votre configuration email et cliquez sur "Sauvegarder"</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</div>
            <div>
              <p className="font-medium">Tester la connexion</p>
              <p>Utilisez le bouton "Tester la connexion" pour vérifier que les paramètres sont corrects</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</div>
            <div>
              <p className="font-medium">Envoyer un email de test</p>
              <p>Saisissez votre adresse email et envoyez un email de test pour valider le fonctionnement</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">4</div>
            <div>
              <p className="font-medium">Notifications automatiques</p>
              <p>Une fois configuré, les emails seront envoyés automatiquement lors des événements de l'application</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de modification de template */}
      <EmailTemplateModal
        template={selectedTemplate}
        isOpen={isTemplateModalOpen}
        onClose={() => {
          setIsTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
        onSuccess={() => {
          loadEmailTemplates();
          setIsTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
      />
    </div>
  );
}